import { AnalyticsBrowser } from '@segment/analytics-next'
import type {
  AgentTraits,
  AnalyticsAdapter,
  AnalyticsConfig,
  AnalyticsPageViewProperties,
  AnalyticsTrackProperties,
} from './analyticsAdapter.ts'
import {
  pickAllowedSegmentProperties,
  SEGMENT_EVENT_NAME_USER_INTERACTED_WITH_ELEMENT,
  SEGMENT_EVENT_NAME_USER_VIEWED_SCREEN,
} from './segmentEventSchema.ts'

export class SegmentAnalyticsAdapter implements AnalyticsAdapter {
  private analytics: AnalyticsBrowser | undefined
  private isInitialized = false

  private mapAppMetaToSegmentProperties(properties: Record<string, unknown>) {
    const appId =
      typeof properties.appId === 'string' ? properties.appId : undefined
    const appVersion =
      typeof properties.appVersion === 'string'
        ? properties.appVersion
        : undefined

    return {
      ...(appId ? { source: appId } : {}),
      ...(appVersion ? { buildId: appVersion } : {}),
    }
  }

  initialize(config: AnalyticsConfig): void {
    if (this.isInitialized) return

    if (!config.writeKey) {
      console.error('[SegmentAnalyticsAdapter]: Missing Segment write key')
      return
    }

    this.analytics = new AnalyticsBrowser()
    this.analytics.load({ writeKey: config.writeKey }).catch((err) => {
      console.error(
        '[SegmentAnalyticsAdapter]: Failed to load Segment SDK:',
        err,
      )
    })

    this.isInitialized = true
  }

  identify(agentId: string, traits?: AgentTraits): void {
    void this.analytics?.identify(agentId, traits)?.catch((error) => {
      console.error(
        '[SegmentAnalyticsAdapter]: Failed to identify agent:',
        error,
      )
    })
  }

  track(eventName: string, properties?: AnalyticsTrackProperties): void {
    const propertiesRecord: Record<string, unknown> = properties ?? {}
    const {
      appId: _appId,
      appVersion: _appVersion,
      ...propertiesWithoutInternalFields
    } = propertiesRecord

    const mergedProperties: Record<string, unknown> = {
      ...propertiesWithoutInternalFields,
      ...this.mapAppMetaToSegmentProperties(propertiesRecord),
      // Preserve local event name in Segment payload.
      action: eventName,
    }

    // Derive element name (Segment-specific).
    const elementName =
      typeof propertiesRecord?.elementName === 'string'
        ? propertiesRecord.elementName
        : typeof propertiesRecord?.buttonId === 'string'
          ? propertiesRecord.buttonId
          : eventName

    mergedProperties.elementName = elementName

    const segmentProperties = pickAllowedSegmentProperties(
      SEGMENT_EVENT_NAME_USER_INTERACTED_WITH_ELEMENT,
      mergedProperties,
    )

    void this.analytics
      ?.track(
        SEGMENT_EVENT_NAME_USER_INTERACTED_WITH_ELEMENT,
        segmentProperties,
      )
      ?.catch((error) => {
        console.error(
          '[SegmentAnalyticsAdapter]: Failed to track event:',
          error,
        )
      })
  }

  pageView(screenName: string, properties?: AnalyticsPageViewProperties): void {
    const propertiesRecord: Record<string, unknown> = properties ?? {}
    const {
      appId: _appId,
      appVersion: _appVersion,
      ...propertiesWithoutInternalFields
    } = propertiesRecord

    const mergedProperties: Record<string, unknown> = {
      ...propertiesWithoutInternalFields,
      ...this.mapAppMetaToSegmentProperties(propertiesRecord),
      screenName,
    }

    const segmentProperties = pickAllowedSegmentProperties(
      SEGMENT_EVENT_NAME_USER_VIEWED_SCREEN,
      mergedProperties,
    )

    // 1) Fire the constrained Track event (Segment schema-approved)
    void this.analytics
      ?.track(SEGMENT_EVENT_NAME_USER_VIEWED_SCREEN, segmentProperties)
      ?.catch((error) => {
        console.error(
          '[SegmentAnalyticsAdapter]: Failed to track screen view:',
          error,
        )
      })

    // 2) Also call Segment `page()` (which produces a Page event in Segment)
    void this.analytics?.page(screenName, segmentProperties)?.catch((error) => {
      console.error('[SegmentAnalyticsAdapter]: Failed to call page():', error)
    })
  }

  reset(): void {
    void this.analytics?.reset()?.catch((error) => {
      console.error(
        '[SegmentAnalyticsAdapter]: Failed to reset analytics:',
        error,
      )
    })
  }
}
